import { useState, useEffect, useCallback } from "react";
import {
  DatePicker,
  Table,
  Tabs,
  Pagination,
  Select,
  Button,
  message,
} from "antd";
import { FileExcelFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Layout from "./Layout";
import {
  getTaskHistory,
  getFaultHistory,
  getLoginHistory,
  downloadTaskHistoryCsv,
  downloadFaultHistoryCsv,
  downloadLoginHistoryCsv,
} from "../services/historyApi";
import { CONFIG } from "../config";
import { SITES } from "../sites";
import "./HistoryPage.css";

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const SITE_OPTIONS = [
  { value: "all", label: "단지 전체", deviceId: null },
  ...SITES.map((s) => ({
    value: s.value,
    label: s.label,
    deviceId: s.deviceId,
  })),
];

const getSiteValue = (deviceId) => {
  const site = SITES.find((s) => s.deviceId === deviceId);
  return site ? site.value : null;
};

const getKoreanLocationName = (poiId, siteValue) => {
  if (!poiId) return null;
  if (siteValue === "1") {
    const poi = CONFIG.POI_LIST.find((p) => p.poiId === poiId);
    if (!poi) return poiId;
    return poi.name
      .replace(/으로 이동$/, "")
      .replace(/로 이동$/, "")
      .replace(/에서 언도킹$/, "");
  }
  if (siteValue === "2" || siteValue === "3") {
    const dongMatch = poiId.match(/^(\d+)dong$/);
    if (dongMatch) return `${dongMatch[1]}동`;
    if (poiId.endsWith("charge")) return "충전위치";
    if (poiId.endsWith("base")) return "시작위치";
  }
  return poiId;
};

const HistoryPage = () => {
  const [activeTab, setActiveTab] = useState("task"); // 'task', 'alarm', or 'login'
  const [selectedSite, setSelectedSite] = useState("all");
  const deviceId = SITE_OPTIONS.find((s) => s.value === selectedSite)?.deviceId;
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "month"),
    dayjs(),
  ]);
  const [taskData, setTaskData] = useState([]);
  const [faultData, setFaultData] = useState([]);
  const [loginData, setLoginData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [faultPagination, setFaultPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loginPagination, setLoginPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadTaskHistoryData = useCallback(
    async (page = 1, pageSize = 10) => {
      if (!dateRange || !dateRange[0] || !dateRange[1]) return;
      setLoading(true);
      try {
        const [start, end] = dateRange;
        const result = await getTaskHistory({
          deviceId,
          startDate: start.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
          page,
          limit: pageSize,
        });

        setTaskData(
          result?.content?.map((item, index) => ({
            key: `${result?.page}-${index}`,
            robotName: item?.robot_name,
            deviceId: item?.device_id,
            createdAt: item?.created_at,
            completedAt: item?.completed_at,
            command: item?.command,
            poiId: item?.poi_id,
            id: item?.user_id || "-",
            result: item?.status,
            errorMessage: item?.error_message,
          })),
        );

        setPagination({
          current: result.page,
          pageSize: result.size,
          total: result.totalElements,
        });
      } catch (error) {
        console.error("Failed to load task history:", error);
        setTaskData([]);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, deviceId],
  );

  const loadFaultHistoryData = useCallback(
    async (page = 1, pageSize = 10) => {
      if (!dateRange || !dateRange[0] || !dateRange[1]) return;

      setLoading(true);
      try {
        const [start, end] = dateRange;
        const result = await getFaultHistory({
          deviceId,
          startDate: start.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
          page,
          limit: pageSize,
        });

        setFaultData(
          result?.content?.map((item, index) => ({
            key: `${result?.page}-${index}`,
            robotName: item?.robot_name,
            deviceId: item?.device_id,
            time: item?.created_at,
            errorCode: item?.errorCode,
            errorMessage: item?.errorMessage,
            cause: item?.cause,
            actionMethod: item?.actionMethod,
          })),
        );

        setFaultPagination({
          current: result.page,
          pageSize: result.size,
          total: result.totalElements,
        });
      } catch (error) {
        console.error("Failed to load fault history:", error);
        setFaultData([]);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, deviceId],
  );

  const loadLoginHistoryData = useCallback(
    async (page = 1, pageSize = 10) => {
      if (!dateRange || !dateRange[0] || !dateRange[1]) return;

      setLoading(true);
      try {
        const [start, end] = dateRange;
        const result = await getLoginHistory({
          deviceId,
          startDate: start.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
          page,
          limit: pageSize,
        });

        setLoginData(
          result?.content?.map((item, index) => ({
            key: `${result?.page}-${index}`,
            time: item?.created_at,
            id: item?.user_id,
            userName: item?.user_name,
            auth: item?.auth,
            success: item?.success,
          })),
        );

        setLoginPagination({
          current: result.page,
          pageSize: result.size,
          total: result.totalElements,
        });
      } catch (error) {
        console.error("Failed to load login history:", error);
        setLoginData([]);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, deviceId],
  );

  useEffect(() => {
    if (activeTab === "task") {
      loadTaskHistoryData(1, pagination.pageSize);
    } else if (activeTab === "alarm") {
      loadFaultHistoryData(1, faultPagination.pageSize);
    } else if (activeTab === "login") {
      loadLoginHistoryData(1, loginPagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    dateRange,
    loadTaskHistoryData,
    loadFaultHistoryData,
    loadLoginHistoryData,
  ]);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleCsvDownload = async (downloadFn, filenamePrefix) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    setDownloadLoading(true);
    try {
      const [start, end] = dateRange;
      const response = await downloadFn({
        deviceId,
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      });
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${filenamePrefix}_${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match) filename = match[1].replace(/['"]/g, "");
      }
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error("다운로드에 실패했습니다.");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Table columns for Task
  const taskColumns = [
    {
      title: "작업 실행 로봇명",
      dataIndex: "robotName",
      key: "robotName",
      align: "left",
    },
    {
      title: "작업 기간",
      key: "period",
      align: "left",
      render: (_, record) => {
        const start = record.createdAt
          ? dayjs
              .utc(record.createdAt)
              .local()
              .format("YYYY년 MM월 DD일 HH:mm:ss")
          : "-";
        const end = record.completedAt
          ? dayjs
              .utc(record.completedAt)
              .local()
              .format("YYYY년 MM월 DD일 HH:mm:ss")
          : "-";
        return `${start} - ${end}`;
      },
    },
    {
      title: "작업 유형",
      dataIndex: "command",
      key: "command",
      align: "left",
      render: (command, record) => {
        const siteValue = getSiteValue(record.deviceId);

        if (siteValue === "1" || !siteValue) {
          if (command === "COMMAND_MOVE") {
            const poi = CONFIG.POI_LIST.find((p) => p.poiId === record.poiId);
            return poi ? poi.name : record.poiId;
          }
          if (command === "COMMAND_STOP") return "정지";
          if (command === "COMMAND_UNLOCK") return "작업 실행 잠금해제";
          return command;
        }

        // 2단지, 3단지
        if (command === "COMMAND_STOP") return "정지";
        if (command === "COMMAND_UNLOCK") return "작업 실행 잠금해제";
        if (command === "COMMAND_DOCK") return "도킹";

        const locationName = getKoreanLocationName(record.poiId, siteValue);
        if (!locationName) return command;

        if (command === "COMMAND_MOVE") {
          const lastCode = locationName.charCodeAt(locationName.length - 1);
          const jongseong = (lastCode - 0xac00) % 28;
          const particle = jongseong !== 0 && jongseong !== 8 ? "으로" : "로";
          return `${locationName}${particle} 이동`;
        }
        if (command === "COMMAND_UNDOCK") {
          return `${locationName}에서 언도킹`;
        }
        return command;
      },
    },
    {
      title: "작업 수행지/이동 목적지",
      dataIndex: "poiId",
      key: "poiId",
      align: "left",
      render: (poiId, record) => {
        if (!poiId) return "-";
        if (record.command === "COMMAND_DOCK") return "-";
        const siteValue = getSiteValue(record.deviceId);
        const locationName = getKoreanLocationName(poiId, siteValue);
        return locationName || poiId;
      },
    },
    {
      title: "작업 실행 사용자 (ID)",
      dataIndex: "id",
      key: "id",
      align: "left",
    },
    {
      title: "작업 결과",
      dataIndex: "result",
      key: "result",
      align: "left",
      render: (status) => {
        const statusMap = {
          success: "성공",
          failed: "실패",
          cancelled: "취소",
          pending: "진행중",
          sent: "진행중",
          executing: "진행중",
        };
        return statusMap[status?.toLowerCase()] || status || "-";
      },
    },
    {
      title: "실패 사유",
      dataIndex: "errorMessage",
      key: "errorMessage",
      align: "left",
      render: (errorMessage) => errorMessage || "-",
    },
  ];

  // Table columns for Fault (에러 이력)
  const faultColumns = [
    {
      title: "에러 발생 로봇명",
      dataIndex: "robotName",
      key: "robotName",
      align: "left",
    },
    {
      title: "일시",
      dataIndex: "time",
      key: "time",
      align: "left",
      render: (text) => {
        if (!text || text === "...") return text;
        return dayjs.utc(text).local().format("YYYY년 MM월 DD일 HH:mm:ss");
      },
    },
    {
      title: "에러 코드",
      dataIndex: "errorCode",
      key: "errorCode",
      align: "left",
    },
    {
      title: "에러 메시지",
      dataIndex: "errorMessage",
      key: "errorMessage",
      align: "left",
    },
    {
      title: "에러 원인",
      dataIndex: "cause",
      key: "cause",
      align: "left",
    },
    {
      title: "조치 방법",
      dataIndex: "actionMethod",
      key: "actionMethod",
      align: "left",
    },
  ];

  // Table columns for login
  const loginColumns = [
    {
      title: "일시",
      dataIndex: "time",
      key: "time",
      align: "left",
      render: (text) => {
        if (!text || text === "...") return text;
        return dayjs.utc(text).local().format("YYYY년 MM월 DD일 HH:mm:ss");
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      align: "left",
    },
    {
      title: "사용자명",
      dataIndex: "userName",
      key: "userName",
      align: "left",
    },
    {
      title: "권한",
      dataIndex: "auth",
      key: "auth",
      align: "left",
    },
    {
      title: "성공/실패",
      dataIndex: "success",
      key: "success",
      align: "left",
      render: (value) => (value ? "성공" : "실패"),
    },
  ];

  return (
    <Layout>
      <div className="history-page">
        <div className="history-content">
          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            className="history-tabs"
            items={[
              {
                key: "task",
                label: "작업 이력",
                children: (
                  <>
                    {/* Filter Section */}
                    <div className="filter-section">
                      <div className="date-filter">
                        <label>조회 단지</label>
                        <Select
                          value={selectedSite}
                          onChange={(value) => setSelectedSite(value)}
                          options={SITE_OPTIONS}
                          className="site-filter-select"
                        />
                      </div>
                      <div className="date-filter">
                        <label>조회 기간</label>
                        <RangePicker
                          value={dateRange}
                          onChange={handleDateChange}
                          format="YYYY.MM.DD"
                          placeholder={["YYYY.MM.DD", "YYYY.MM.DD"]}
                          className="date-range-picker"
                          disabledDate={(current) =>
                            current && current.isAfter(dayjs(), "day")
                          }
                        />
                      </div>
                      <Button
                        icon={
                          <FileExcelFilled
                            style={{
                              color: "rgba(0, 0, 0, 0.88)",
                              fontSize: 16,
                            }}
                          />
                        }
                        style={{ marginLeft: "auto" }}
                        loading={downloadLoading}
                        onClick={() =>
                          handleCsvDownload(downloadTaskHistoryCsv, "작업이력")
                        }
                      >
                        원본데이터 다운받기
                      </Button>
                    </div>
                    <div className="data-table-container">
                      <Table
                        columns={taskColumns}
                        dataSource={taskData}
                        loading={loading}
                        pagination={false}
                        scroll={{ y: "calc(100vh - 400px)" }}
                        className="ant-history-table"
                      />
                    </div>
                    <div className="pagination-container">
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showTotal={(total) => `총 ${total}건`}
                        showLessItems
                        onChange={(page, pageSize) =>
                          loadTaskHistoryData(page, pageSize)
                        }
                      />
                    </div>
                  </>
                ),
              },
              {
                key: "alarm",
                label: "에러 이력",
                children: (
                  <>
                    {/* Filter Section */}
                    <div className="filter-section">
                      <div className="date-filter">
                        <label>조회 단지</label>
                        <Select
                          value={selectedSite}
                          onChange={(value) => setSelectedSite(value)}
                          options={SITE_OPTIONS}
                          className="site-filter-select"
                        />
                      </div>
                      <div className="date-filter">
                        <label>조회 기간</label>
                        <RangePicker
                          value={dateRange}
                          onChange={handleDateChange}
                          format="YYYY.MM.DD"
                          placeholder={["YYYY.MM.DD", "YYYY.MM.DD"]}
                          className="date-range-picker"
                          disabledDate={(current) =>
                            current && current.isAfter(dayjs(), "day")
                          }
                        />
                      </div>
                      <Button
                        icon={
                          <FileExcelFilled
                            style={{
                              color: "rgba(0, 0, 0, 0.88)",
                              fontSize: 16,
                            }}
                          />
                        }
                        style={{ marginLeft: "auto" }}
                        loading={downloadLoading}
                        onClick={() =>
                          handleCsvDownload(downloadFaultHistoryCsv, "에러이력")
                        }
                      >
                        원본데이터 다운받기
                      </Button>
                    </div>
                    <div className="data-table-container">
                      <Table
                        columns={faultColumns}
                        dataSource={faultData}
                        loading={loading}
                        pagination={false}
                        scroll={{ y: "calc(100vh - 400px)" }}
                        className="ant-history-table"
                      />
                    </div>
                    <div className="pagination-container">
                      <Pagination
                        current={faultPagination.current}
                        pageSize={faultPagination.pageSize}
                        total={faultPagination.total}
                        showTotal={(total) => `총 ${total}건`}
                        showSizeChanger={false}
                        showLessItems
                        onChange={(page, pageSize) =>
                          loadFaultHistoryData(page, pageSize)
                        }
                      />
                    </div>
                  </>
                ),
              },
              {
                key: "login",
                label: "로그인 이력",
                children: (
                  <>
                    {/* Filter Section */}
                    <div className="filter-section">
                      <div className="date-filter">
                        <label>조회 기간</label>
                        <RangePicker
                          value={dateRange}
                          onChange={handleDateChange}
                          format="YYYY.MM.DD"
                          placeholder={["YYYY.MM.DD", "YYYY.MM.DD"]}
                          className="date-range-picker"
                          disabledDate={(current) =>
                            current && current.isAfter(dayjs(), "day")
                          }
                        />
                      </div>
                      <Button
                        icon={
                          <FileExcelFilled
                            style={{
                              color: "rgba(0, 0, 0, 0.88)",
                              fontSize: 16,
                            }}
                          />
                        }
                        style={{ marginLeft: "auto" }}
                        loading={downloadLoading}
                        onClick={() =>
                          handleCsvDownload(
                            downloadLoginHistoryCsv,
                            "로그인이력",
                          )
                        }
                      >
                        원본데이터 다운받기
                      </Button>
                    </div>
                    <div className="data-table-container">
                      <Table
                        columns={loginColumns}
                        dataSource={loginData}
                        loading={loading}
                        pagination={false}
                        scroll={{ y: "calc(100vh - 400px)" }}
                        className="ant-history-table"
                        showSizeChanger={false}
                      />
                    </div>
                    <div className="pagination-container">
                      <Pagination
                        current={loginPagination.current}
                        pageSize={loginPagination.pageSize}
                        total={loginPagination.total}
                        showTotal={(total) => `총 ${total}건`}
                        onChange={(page, pageSize) =>
                          loadLoginHistoryData(page, pageSize)
                        }
                        showSizeChanger={false}
                        showLessItems
                      />
                    </div>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default HistoryPage;
